import { useId } from "react";

import { getErrorMessage, isAPIError } from "@/core/services";
import { isEmbarcacaoFichaInvalida } from "@/modules/embarcacoes/embarcacoes.gateway";
import type {
  EmbarcacaoDetailStatus,
  EmbarcacaoFicha,
} from "@/modules/embarcacoes/embarcacoes.types";
import { useEmbarcacaoFicha } from "./use-embarcacao-ficha";
import { useDetailLoadItem } from "./use-embarcacao-detail-load-gate";

export const DETAIL_SESSION_EXPIRED_MESSAGE =
  "Sua sessão expirou. Entre novamente para consultar os detalhes da embarcação.";
export const DETAIL_FORBIDDEN_MESSAGE =
  "Você não tem permissão para consultar os detalhes desta embarcação.";
export const DETAIL_NOT_FOUND_MESSAGE = "Embarcação não encontrada.";

export interface EmbarcacaoDetailSectionQuery<T> {
  /** Bloco da seção dentro da ficha completa carregada pelo `EmbarcacaoFichaProvider`. */
  readonly select: (ficha: EmbarcacaoFicha) => T;
  readonly isEmpty: (data: T) => boolean;
  /** Nome da seção com artigo, para a mensagem de falha (ex.: "a Classe", "os Tanques"). */
  readonly secao: string;
}

export interface EmbarcacaoDetailSectionState<T> {
  status: EmbarcacaoDetailStatus;
  data: T | null;
  errorMessage: string | null;
}

export interface UseEmbarcacaoDetailSectionResult<T> extends EmbarcacaoDetailSectionState<T> {
  reload: () => void;
}

const MESSAGE_BY_STATUS = {
  sessionExpired: DETAIL_SESSION_EXPIRED_MESSAGE,
  forbidden: DETAIL_FORBIDDEN_MESSAGE,
  notFound: DETAIL_NOT_FOUND_MESSAGE,
} as const;

/**
 * Casca de uma seção do detalhe: a consulta é única e vem da ficha (`useEmbarcacaoFicha`); aqui entram os estados da
 * seção (401, 404, vazio) e as mensagens, que são as mesmas em todas elas. Cada falha devolve `data: null`, para que uma
 * carga incompleta não seja lida como a embarcação consultada, e o `reload` refaz a ficha inteira.
 */
export function useEmbarcacaoDetailSection<T>(
  id: string,
  { select, isEmpty, secao }: EmbarcacaoDetailSectionQuery<T>,
): UseEmbarcacaoDetailSectionResult<T> {
  const carga = useEmbarcacaoFicha();
  const { reload } = carga;
  // Uma ficha de outra embarcação nunca é lida como a desta.
  const status = carga.embarcacaoId === id ? carga.status : "loading";
  useDetailLoadItem(useId(), status === "loading");

  if (status === "loading") return { status, data: null, errorMessage: null, reload };
  if (status === "forbidden") {
    return { status, data: null, errorMessage: DETAIL_FORBIDDEN_MESSAGE, reload };
  }
  if (status === "error" || !carga.ficha) {
    const httpStatus = isAPIError(carga.error) ? carga.error.status : null;
    const failure = httpStatus === 401 ? "sessionExpired" : httpStatus === 404 ? "notFound" : null;
    if (failure) {
      return { status: failure, data: null, errorMessage: MESSAGE_BY_STATUS[failure], reload };
    }

    // O detalhe técnico de uma ficha fora do contrato não é para o usuário.
    const message = isEmbarcacaoFichaInvalida(carga.error) ? null : getErrorMessage(carga.error);

    return {
      status: "error",
      data: null,
      errorMessage: message || `Não foi possível carregar ${secao} da embarcação.`,
      reload,
    };
  }

  const data = select(carga.ficha);

  return isEmpty(data)
    ? { status: "empty", data: null, errorMessage: null, reload }
    : { status: "success", data, errorMessage: null, reload };
}
